/**
 * AdoptionAnalytics — Admin → Adoption tab.
 *
 * Full adoption analytics (ported from the standalone SPA): executive Adoption
 * report, usage + loyalty, per-company drill-down, prompt categories, product
 * metrics, and trends/distribution. Data comes from the live backend via
 * AdoptionDataProvider (/api/usage + /api/usage/categorize), not S3 snapshots.
 */
import { useState, useEffect } from 'react';
import { AdoptionDataProvider, useData } from './adoption/AdoptionData.jsx';
import AdoptionReport from './adoption/views/AdoptionReport.jsx';
import GrowthConcentration from './adoption/views/GrowthConcentration.jsx';
import EngagementActivation from './adoption/views/EngagementActivation.jsx';
import Overview from './adoption/views/Overview.jsx';
import Companies from './adoption/views/Companies.jsx';
import CompanyDetail from './adoption/views/CompanyDetail.jsx';
import PromptCategories from './adoption/views/PromptCategories.jsx';
import Metrics from './adoption/views/Metrics.jsx';
import Trends from './adoption/views/Trends.jsx';
import './adoption/adoption.css';

const TABS = [
  ['adoption', 'Adoption'],
  ['growth', 'Growth & concentration'],
  ['engagement', 'Engagement & activation'],
  ['usage', 'Usage & loyalty'],
  ['companies', 'Companies'],
  ['prompts', 'Prompt categories'],
  ['metrics', 'Metrics'],
  ['trends', 'Trends'],
];

function GlobalReport() {
  const { events, aliases } = useData();
  return <AdoptionReport events={events} aliases={aliases} scope="global" />;
}

function Inner() {
  const {
    loading, error, events, mergeMerck, setMergeMerck, days, setDays, selectedCompany, reload,
  } = useData();
  const [tab, setTab] = useState('adoption');

  // Decouple the day-range box from the committed `days` value: edit freely
  // (clear, retype) with no clamping or auto-fetch per keystroke; commit only on
  // Load/Enter, which is the single thing that triggers a refetch.
  const [daysInput, setDaysInput] = useState(String(days));
  useEffect(() => { setDaysInput(String(days)); }, [days]);
  const commitDays = () => {
    const n = parseInt(daysInput, 10);
    if (Number.isFinite(n)) {
      const clamped = Math.max(1, Math.min(365, n));
      setDaysInput(String(clamped));
      if (clamped !== days) setDays(clamped);
    } else {
      setDaysInput(String(days)); // empty/invalid → revert to last committed
    }
  };

  return (
    <div className="adopt-root">
      <div className="adopt-controls">
        <div className="adopt-subtabs">
          {TABS.map(([k, l]) => (
            <button key={k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>{l}</button>
          ))}
        </div>
        <label className="adopt-chk">
          <input type="checkbox" checked={mergeMerck} onChange={(e) => setMergeMerck(e.target.checked)} /> Merge EMD/Merck
        </label>
        <label className="adopt-days">Last
          <input
            type="number" min="1" max="365" value={daysInput}
            onChange={(e) => setDaysInput(e.target.value)}
            onFocus={(e) => e.target.select()}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitDays(); } }}
            aria-label="Number of days to analyze"
          /> days
        </label>
        <button type="button" className="adopt-load" onClick={commitDays}>Load</button>
      </div>

      {loading && <div className="status">Loading usage…</div>}
      {error && (
        <div className="status err">
          Couldn't load usage — the server may be temporarily unavailable ({error}).{' '}
          <button type="button" className="adopt-retry" onClick={reload} disabled={loading}>Retry</button>
        </div>
      )}
      {!loading && !error && !events.length && <div className="status">No external usage in the selected window.</div>}

      {!loading && !error && events.length > 0 && (
        <div className="adopt-view">
          {tab === 'adoption' && <GlobalReport />}
          {tab === 'growth' && <GrowthConcentration />}
          {tab === 'engagement' && <EngagementActivation />}
          {tab === 'usage' && <Overview />}
          {tab === 'companies' && (selectedCompany ? <CompanyDetail /> : <Companies />)}
          {tab === 'prompts' && <PromptCategories />}
          {tab === 'metrics' && <Metrics />}
          {tab === 'trends' && <Trends />}
        </div>
      )}
    </div>
  );
}

const AdoptionAnalytics = ({ authData }) => (
  <AdoptionDataProvider authData={authData}><Inner /></AdoptionDataProvider>
);

export default AdoptionAnalytics;
